import MaterialExplorer from "@/components/MaterialExplorer";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import DataFilters from "@/components/DataFilters";
import SearchResults from "@/components/HierarchySearchResults";
import HierarchyTree from "@/components/HierarchyTree";
import ParameterList from "@/components/ParameterList";
import ParameterDetail from "@/components/ParameterDetail";
import { Globe, ArrowRight, UploadSimple, Sparkle, Warning } from "@phosphor-icons/react";
import { endpoints } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import StatusBadge from "@/components/StatusBadge";

export default function Explorer() {
    const [selection, setSelection] = useState(null);
    const [concept, setConcept] = useState(null);   // selected parameter concept
    const [params, setParams] = useSearchParams();
    const q = params.get("q");
    const collection = params.get("collection");
    const year = params.get("year") || "";
    const geography = params.get("geography") || "";
    const verification = params.get("verification") || "";
    const filters = {year, geography, verification};
    const [facets, setFacets] = useState({});
    useEffect(() => { endpoints.searchFacets().then(setFacets).catch(() => {}); }, []);
    const changeFilters = values => {
        const next = new URLSearchParams(params);
        ["year", "geography", "verification"].forEach(key => values[key] ? next.set(key, values[key]) : next.delete(key));
        setParams(next);
    };
    const parameterId = params.get("parameter");
    const entryId = params.get("entry");
    const processId = params.get("process");
    const [searchResults, setSearchResults] = useState(null);

    const [searchError, setSearchError] = useState("");
    const entityKind = params.get("kind");
    const entityId = params.get("entity_id");
    useEffect(() => {
        let active = true;
        setSearchResults(null);
        setSearchError("");
        setSelection(null);
        setConcept(null);
        if (entryId || parameterId || processId) {
            endpoints.resolveSearchResult(entryId ? "community_dataset" : parameterId ? "parameter" : "process", entryId || parameterId || processId)
                .then(result => { if (active) { setSelection(result.selection); setConcept(result.concept); } })
                .catch(() => { if (active) setSearchError("This entry is unavailable or has not been published."); });
        }
        return () => { active = false; };
    }, [parameterId, entryId, processId]);

    useEffect(() => {
        if (entryId || parameterId || processId) return;
        let active = true;
        setSearchResults(null);
        setSearchError("");
        if (q) endpoints.search(q, { collection, kind: entityKind, entity_id: entityId, year: year || undefined, geography: geography || undefined, verification: verification || undefined })
            .then(data => { if (active) setSearchResults(data); })
            .catch(() => { if (active) setSearchError("Search could not be loaded. Please try again."); });
        return () => { active = false; };
    }, [q, collection, entityKind, entityId, parameterId, entryId, processId, year, geography, verification]);

    const handleSelectProcess = sel => { const next = new URLSearchParams(params); ["q", "collection", "kind", "entity_id", "parameter", "entry"].forEach(key => next.delete(key)); next.set("process", sel.process.id); setParams(next); };
    const handleSearchResult = result => {
        const next = new URLSearchParams(params);
        next.delete("entry"); next.delete("process");
        next.set("parameter", result.id);
        setParams(next);
    };
    const back = () => {
        if (q) { const next = new URLSearchParams(params); next.delete("parameter"); next.delete("entry"); next.delete("process"); setParams(next); }
        else if (selection) { const next = new URLSearchParams(params); next.delete("parameter"); next.delete("entry"); next.set("process", selection.process.id); setParams(next); }
    };

    if (!parameterId && !entryId && !processId) return <MaterialExplorer />;

    return (
        <div className="sdx-explore-compact w-full grid grid-cols-12 min-h-[calc(100vh-4rem)]">
            <aside className="col-span-12 md:col-span-4 lg:col-span-3 xl:col-span-2 border-r border-neutral-200 bg-white h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto">
                <HierarchyTree
                    activeSelection={selection}
                    activeConcept={concept}
                    selectedProcessId={selection?.process?.id}
                    onSelectProcess={handleSelectProcess}
                />
            </aside>

            <section
                data-testid={TEST_IDS.detailPanel}
                className="col-span-12 md:col-span-8 lg:col-span-9 xl:col-span-10 bg-white min-w-0"
            >
                {!selection && <div className="px-6 lg:px-10"><DataFilters value={filters} facets={facets} onChange={changeFilters} onClear={() => changeFilters({year:"",geography:"",verification:""})} /></div>}
                {(q || parameterId || entryId || processId) && !searchResults && !selection ? (
                    <div className="p-10" role="status">{searchError || "Searching the directory…"}</div>
                ) : searchResults && !selection ? (
                    <SearchResults key={`${collection}:${q}:${entityKind}:${entityId}:${year}:${geography}:${verification}`} options={{ collection, year: year || undefined, geography: geography || undefined, verification: verification || undefined, kind: entityKind, entity_id: entityId }} q={q} data={searchResults} onOpen={handleSearchResult} />
                ) : selection ? (
                    selection.process.has_data === false ? (
                        <ProcessEmpty selection={selection} />
                    ) : concept ? (
                        <ParameterDetail
                            key={`${concept.id}:${entryId || ""}`}
                            entryId={entryId}
                            filters={filters}
                            onFiltersChange={changeFilters}
                            selection={selection}
                            concept={concept}
                            backLabel={q ? "Search results" : "Parameters"}
                            onBack={back}
                        />
                    ) : (
                        <ParameterList
                            selection={selection}
                            onSelectConcept={(c) => handleSearchResult({ id: c.id })}
                        />
                    )
                ) : (
                    <EmptyState />
                )}
            </section>
        </div>
    );
}

const ProcessEmpty = ({ selection }) => (
    <div data-testid={TEST_IDS.processEmptyState} className="p-8">
        <nav className="text-xs text-neutral-500 flex flex-wrap items-center gap-2 pb-4">
            <span>{selection.country.flag}</span>
            <span>{selection.country.name}</span>
            <span className="text-neutral-300">/</span>
            <span>{selection.material.name}</span>
            <span className="text-neutral-300">/</span>
            <span className="text-black font-medium">{selection.process.name}</span>
        </nav>
        <div className="border border-dashed border-orange-300 bg-orange-50/50 p-10 text-center rounded-sm">
            <div className="w-14 h-14 mx-auto mb-5 bg-white border border-orange-300 flex items-center justify-center rounded-sm">
                <Warning size={26} weight="fill" className="text-orange-500" />
            </div>
            <div className="mb-3">
                <StatusBadge status="missing_data" size="lg" />
            </div>
            <h2 className="font-serif text-3xl font-light">
                No verified sustainability data is currently available
            </h2>
            <p className="mt-3 text-neutral-600 max-w-lg mx-auto">
                Be the first contributor to add a trusted source for the{" "}
                <span className="font-medium">{selection.process.name}</span> process.
            </p>
            <Link
                to={`/submit?path=${encodeURIComponent(
                    `${selection.country.name} > ${selection.category.name} > ${selection.material.name} > ${selection.material_type.name} > ${selection.process.name}`
                )}`}
                data-testid={TEST_IDS.contributeCta}
                className="btn-secondary mt-6 px-5 py-3 text-base"
            >
                <UploadSimple size={16} weight="bold" /> Contribute a Trusted Source
            </Link>
        </div>
    </div>
);

const EmptyState = () => (
    <div
        data-testid={TEST_IDS.emptyState}
        className="h-full flex flex-col items-center justify-center px-8 py-20 text-center"
    >
        <div className="relative mb-8">
            <div className="w-20 h-20 border border-[#6B46C1] bg-[#F5F3FF] flex items-center justify-center rounded-sm">
                <Globe size={32} weight="duotone" className="text-[#6B46C1]" />
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#FFD60A] border border-black flex items-center justify-center rounded-sm">
                <Sparkle size={12} weight="fill" />
            </div>
        </div>
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight max-w-2xl">
            Every parameter is a living repository of evidence.
        </h2>
        <p className="mt-4 text-neutral-500 max-w-lg text-sm leading-relaxed">
            Drill from country to process, pick an environmental parameter, then compare
            values submitted by governments, industry, academics and independent researchers.
            All sources remain visible, traceable and community-validated.
        </p>
        <div className="mt-8 flex items-center gap-2 text-xs text-neutral-400 flex-wrap justify-center">
            <span>Country</span> <ArrowRight size={10} /> <span>Category</span>{" "}
            <ArrowRight size={10} /> <span>Material</span>{" "}
            <ArrowRight size={10} /> <span>Type</span> <ArrowRight size={10} />{" "}
            <span>Process</span> <ArrowRight size={10} />{" "}
            <span>Parameter</span> <ArrowRight size={10} />{" "}
            <span className="text-[#6B46C1] font-semibold">Community datasets</span>
        </div>
    </div>
);
